// The `rich_content` block — ONE renderer for every surface (designer canvas,
// live preview / Run view, the downloaded runtime and the static-site SSR), so
// a RICHTEXT body looks the same wherever it is shown.
//
// Pure: resolves the block's `source` binding against the surface's vars,
// sanitises + decorates the HTML (`prepareRichContent`), and renders. No
// hooks, no browser APIs — safe under renderToString.

import type { RichContentBlock } from '@twasta/page-schema'
import { substituteVars, type VarSource } from './actions/substitute'
import { prepareRichContent } from './richContent'

export interface RichContentRendererProps {
  block: RichContentBlock
  vars: VarSource
  /** Surface-specific post-processing of the sanitised HTML (the SSR uses it
   *  to point internal links at real published page URLs). */
  transformHtml?: (html: string) => string
  /** Shown instead of `emptyText` when the binding resolved to nothing —
   *  the designer canvas uses it to explain what the block is bound to. */
  placeholder?: React.ReactNode
}

export function resolveRichSource(source: string | undefined, vars: VarSource): string {
  const src = (source || '').trim()
  if (!src) return ''
  try {
    // A binding with no value is left in place by substituteVars; on a
    // public page that would print "{{item.body}}", so it reads as empty.
    return substituteVars(src, vars).replace(/\{\{\s*[^{}]+?\s*\}\}/g, '')
  } catch (err) {
    console.error('[rich_content] could not resolve source binding', src, err)
    return ''
  }
}

export function RichContentRenderer({ block, vars, transformHtml, placeholder }: RichContentRendererProps) {
  const raw = resolveRichSource(block.source, vars)
  const prepared = prepareRichContent(raw, {
    externalLinksNewTab: !!block.externalLinksNewTab,
    nofollowExternal: !!block.nofollowExternal,
  })
  let html = prepared.html
  if (transformHtml) {
    try { html = transformHtml(html) } catch (err) {
      console.error('[rich_content] transformHtml failed', err)
    }
  }
  const compact = block.typography === 'compact'
  const showToc = block.toc === true
    ? prepared.toc.length > 0
    : block.toc === 'auto' ? prepared.toc.length >= 3 : false

  const cls = ['pg-rich-content', compact ? 'pg-rich-content--compact' : 'pg-rich-content--article']
  if (block.readableWidth !== false && !compact) cls.push('pg-rich-content--readable')

  if (!html.trim()) {
    return (
      <div className={cls.join(' ')}>
        <style>{RICH_CONTENT_CSS}</style>
        {placeholder ?? (block.emptyText
          ? <p className="pg-rich-content__empty">{block.emptyText}</p>
          : null)}
      </div>
    )
  }
  return (
    <div className={cls.join(' ')}>
      <style>{RICH_CONTENT_CSS}</style>
      {showToc && (
        <nav className="pg-rich-toc" aria-label={block.tocTitle || 'Contents'}>
          <p className="pg-rich-toc__title">{block.tocTitle || 'Contents'}</p>
          <ol>
            {prepared.toc.map(e => (
              <li key={e.id} className={`pg-rich-toc__l${e.level}`}>
                <a href={`#${e.id}`}>{e.text}</a>
              </li>
            ))}
          </ol>
        </nav>
      )}
      <div className="pg-rich-content__body" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  )
}

// Theme-token typography (`--pg-*`), so a body follows the site's theme.
// Selectors are scoped to `.pg-rich-content`; repeated <style> tags from
// several blocks on one page are identical and harmless.
export const RICH_CONTENT_CSS = `
.pg-rich-content { color: var(--pg-text); min-width: 0; }
.pg-rich-content--readable { max-width: 72ch; }
.pg-rich-content--article .pg-rich-content__body { font-size: 1.0625rem; line-height: 1.75; }
.pg-rich-content--compact .pg-rich-content__body { font-size: 0.95rem; line-height: 1.55; }
.pg-rich-content__body > :first-child { margin-top: 0; }
.pg-rich-content__body > :last-child { margin-bottom: 0; }
.pg-rich-content__body p { margin: 0 0 1em; }
.pg-rich-content__body h2 { font-size: 1.6em; line-height: 1.25; font-weight: 700; margin: 1.6em 0 0.5em; scroll-margin-top: 80px; }
.pg-rich-content__body h3 { font-size: 1.3em; line-height: 1.3; font-weight: 650; margin: 1.4em 0 0.4em; scroll-margin-top: 80px; }
.pg-rich-content__body h4 { font-size: 1.1em; font-weight: 650; margin: 1.2em 0 0.3em; scroll-margin-top: 80px; }
.pg-rich-content__body ul, .pg-rich-content__body ol { margin: 0 0 1em; padding-left: 1.5em; }
.pg-rich-content__body li + li { margin-top: 0.3em; }
.pg-rich-content__body a { color: var(--pg-primary); text-decoration: underline; text-underline-offset: 2px; }
.pg-rich-content__body blockquote { margin: 1.2em 0; padding: 0.2em 0 0.2em 1em; border-left: 3px solid var(--pg-primary); color: var(--pg-text-secondary, var(--pg-muted, inherit)); font-style: italic; }
.pg-rich-content__body pre { margin: 1.2em 0; padding: 14px 16px; background: var(--pg-surface); border: 1px solid var(--pg-border); border-radius: 8px; overflow-x: auto; font-size: 0.875em; line-height: 1.5; }
.pg-rich-content__body code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.9em; }
.pg-rich-content__body :not(pre) > code { background: var(--pg-surface); padding: 0.1em 0.35em; border-radius: 4px; }
.pg-rich-content__body img { display: block; max-width: 100%; height: auto; margin: 1.4em auto; border-radius: 8px; }
.pg-rich-content__body figure { margin: 1.4em 0; }
.pg-rich-content__body figcaption { text-align: center; font-size: 0.875em; color: var(--pg-text-secondary, inherit); margin-top: 0.4em; }
.pg-rich-content__body hr { border: none; border-top: 1px solid var(--pg-border); margin: 2em 0; }
.pg-rich-content__body table { width: 100%; border-collapse: collapse; margin: 1.2em 0; font-size: 0.95em; display: block; overflow-x: auto; }
.pg-rich-content__body th, .pg-rich-content__body td { border: 1px solid var(--pg-border); padding: 8px 10px; text-align: left; vertical-align: top; }
.pg-rich-content__body th { background: var(--pg-surface); font-weight: 600; }
.pg-rich-content__empty { color: var(--pg-text-secondary, inherit); font-style: italic; margin: 0; }
.pg-rich-toc { margin: 0 0 1.6em; padding: 12px 16px; border: 1px solid var(--pg-border); border-radius: 8px; background: var(--pg-surface); font-size: 0.95rem; }
.pg-rich-toc__title { margin: 0 0 6px; font-weight: 700; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--pg-text-secondary, inherit); }
.pg-rich-toc ol { margin: 0; padding-left: 1.2em; }
.pg-rich-toc li { margin: 3px 0; }
.pg-rich-toc__l3 { margin-left: 1em !important; list-style: circle; }
.pg-rich-toc__l4 { margin-left: 2em !important; list-style: square; }
.pg-rich-toc a { color: var(--pg-text); text-decoration: none; }
.pg-rich-toc a:hover { color: var(--pg-primary); text-decoration: underline; }
`
